import { beLinkRequest } from '@be-link/request';

type ApiEnv = 'development' | 'testing' | 'production';

const NODE_ENV: ApiEnv = (process.env.NODE_ENV as ApiEnv) || 'development';

export const mockRunResponse = (nodes: any) => ({
  taskId: '123',
  status: 'failed',
  startTime: '2024-01-01T00:00:00Z',
  endTime: '2024-01-01T00:05:00Z',
  nodes: nodes.map((node: any) => {
    const commonOutput = {
      executionTime: '00:00:30',
      logs: ['Node execution started', 'Node execution completed'],
      error: null,
    };

    const startTime = new Date('2024-01-01T00:00:00Z');
    const endTime = new Date('2024-01-01T00:05:00Z');
    const timeCost = endTime.getTime() - startTime.getTime();

    if (node.type === 'sql') {
      return {
        ...node,
        status: 'failed',
        terminated: true,
        startTime: startTime.getTime(),
        timeCost: timeCost,
        outputs: {
          ...commonOutput,
          result: 'Query executed successfully. Result count: 10',
          query: 'select * from user;',
        },
      };
    }

    return {
      ...node,
      status: 'succeeded',
      terminated: true,
      startTime: '2024-01-01T00:00:00Z',
      timeCost: timeCost,
      outputs: {
        ...commonOutput,
        result: 'Node executed successfully',
      },
    };
  }),
});

const DitingServerAddress: Record<ApiEnv, string> = {
  development: 'https://diting-api-dev.8848top.com',
  testing: 'https://diting-api-dev.8848top.com',
  production: 'https://diting-api.8848top.com',
};

export const Service = {
  DitingServer: {
    path: DitingServerAddress[NODE_ENV],
    taskPath: '/task',
    taskRecordPath: '/task-record',
    taskUrl: (path: string) => Service.DitingServer.path + Service.DitingServer.taskPath + path,
  },
};

/**
 * 获取任务详情
 * @param param 获取任务详情请求参数
 * @returns 任务详细信息
 */
export async function getTaskDetail(param: { id: string }) {
  const res = await beLinkRequest.request<ITask>({
    url: Service.DitingServer.taskUrl('/get-detail'),
    data: param,
  });
  return res.data;
}

/**
 * 获取任务详情
 * @param param 获取任务详情请求参数
 * @returns 任务详细信息
 */
export async function getTaskDetailV2(param: { taskId: string }) {
  const res = await beLinkRequest.request<TaskV2Dto>({
    url: Service.DitingServer.taskUrl('/get-detail-v2'),
    data: param,
  });
  return res.data;
}

/**
 * 执行任务
 * @param param 执行任务请求参数
 * @returns 执行任务结果
 */
export async function runTask(param: { id: string }) {
  const res = await beLinkRequest.request<ITask>({
    url: Service.DitingServer.taskUrl('/run'),
    data: param,
  });
  return res.data;
}

/**
 * 取消任务
 * @param param 取消任务请求参数
 * @returns 取消任务结果
 */
export async function cancelTask(param: { id: string }) {
  const res = await beLinkRequest.request<ITask>({
    url: Service.DitingServer.taskUrl('/cancel'),
    data: param,
  });
  return res.data;
}

/**
 * 保存任务
 * @param param 保存任务请求参数
 * @returns 保存任务结果
 */
export async function saveTask(param: { id: string }) {
  const res = await beLinkRequest.request<ITask>({
    url: Service.DitingServer.taskUrl('/save'),
    data: param,
  });
  return res.data;
}

/**
 * 查询workflow任务节点详情
 * @param param 查询workflow任务节点详情请求参数
 * @returns workflow任务节点详情
 */
export async function getWorkflowDetail(param: { taskId: string; executorName: string }) {
  const res = await beLinkRequest.request<IGetWorkflowResponse>({
    url: Service.DitingServer.taskUrl('/get-workflow'),
    data: param,
  });
  return res.data;
}

/**
 * 保存工作流任务节点
 * @param param 保存工作流任务节点请求参数
 * @returns 保存工作流任务节点结果
 */
export async function saveWorkflow(param: ISaveWorkflowRequest) {
  const res = await beLinkRequest.request<ISaveWorkflowRequest>({
    url: Service.DitingServer.taskUrl('/save-workflow'),
    data: param,
  });
  return res.data;
}

/**
 * 执行任务工作流
 * @param param 执行任务工作流请求参数
 * @returns 执行任务工作流结果，返回taskRecordId
 */
export async function executeWorkflow(param: IExecuteWorkflowRequest) {
  const res = await beLinkRequest.request<{ taskRecordId: string }>({
    url: Service.DitingServer.taskUrl('/execute-workflow'),
    data: param,
  });
  return res.data;
}

/**
 * 根据任务记录ID查询工作流详情
 * @param param 查询工作流详情请求参数
 * @returns 工作流执行详情
 */
export async function getWorkflowByTaskRecordId(param: { taskRecordId: string }) {
  const res = await beLinkRequest.request<IExecuteWorkflowResponse>({
    url: Service.DitingServer.path + '/workflow/query-by-task-record-id',
    data: param,
  });
  return res.data;
}
